import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CreatePatientTransactionDto,
  CreateRechnungDto,
  CreateRechnungDtoPdfDecorationEnum,
  DebitPermissionDto,
  PatientBillInfoDto,
} from "../../../../api-client";
import {
  useCreatePatientRechnung,
  useCreatePatientTransaction,
} from "../../../../stores/Transactions/transaction.mutations";
import notificationStore from "../../../../stores/Notification/notificationStore";
import {
  paymentStatus,
  TarifGroupIds,
  transactionMethods,
  transactionTypes,
} from "../../../Transactions/Kasse/v2/types/kasse.types";
import { useLocationId, useUserId } from "../../../../stores/User/User.hooks";

export enum PaymentDialogType {
  NONE = "none",
  PAY = "pay",
  DEBIT = "debit",
  RETURNTOPATIENT = "returnToPatient",
  EXTERNALBILL = "externalBill",
}

export type DialogState =
  | { type: PaymentDialogType.NONE }
  | { type: PaymentDialogType.PAY; rxId: string }
  | { type: PaymentDialogType.DEBIT; rxId: string; permission: DebitPermissionDto }
  | { type: PaymentDialogType.RETURNTOPATIENT; rxId: string }
  | { type: PaymentDialogType.EXTERNALBILL; rxId: string };

type Deps = {
  patientId: number;
  patientHasBillingEmail?: boolean;
};

/**
 * Custom hook to manage patient billing actions.
 * @param Deps - Dependencies for the hook.
 * @returns An object containing billing action handlers and state.
 */
export function usePatientBillingActions({ patientId, patientHasBillingEmail }: Deps) {
  /**
   * Hooks and Mutations coming from transction.mutations.ts
   */
  const userId = useUserId();
  const locationId = useLocationId();
  const createRechnungMut = useCreatePatientRechnung();
  const createTxMut = useCreatePatientTransaction();

  /**
   * Whether generated invoices should include the red header/footer bands.
   *
   * Defaults to "patient has a billing email" (alternative-billing → primary, resolved
   * on the backend) and is re-applied whenever the selected patient changes. A manual
   * toggle afterwards sticks, because the resolved flag is stable for a given patient
   * (a query refetch returns the same boolean, so this effect does not re-run).
   */
  const [withLetterhead, setWithLetterhead] = useState<boolean>(false);
  useEffect(() => {
    setWithLetterhead(patientHasBillingEmail ?? false);
  }, [patientId, patientHasBillingEmail]);

  /**
   * SEPA menu state
   */
  const [sepaMenuState, setSepaMenuState] = useState<{
    anchorEl: HTMLElement | null;
    openMenus: { [key: string]: boolean };
  }>({
    anchorEl: null,
    openMenus: {},
  });

  /**
   * For dialog box management
   */
  const [dialog, setDialog] = useState<DialogState>({ type: PaymentDialogType.NONE });
  const closeDialogs = useCallback(() => setDialog({ type: PaymentDialogType.NONE }), []);

  /**
   * This function initiates the payment process for a given prescription (rxId).
   * Used in UI: "Zahlen" button (Patient Billing Tab)
   */
  const handlePay = useCallback(
    async (bill: PatientBillInfoDto) => {
      if (bill.isEstimated && !bill.isTimeSlotZero) {
        notificationStore.showMessage(
          `Zahlung für Rx-Nr. ${bill.rxNumber} nicht möglich: Bitte planen Sie alle Termine zuerst.`,
          "error",
        );
        return;
      }
      setDialog({ type: PaymentDialogType.PAY, rxId: bill.rxId });
    },
    [setDialog],
  );

  /**
   * This function handles the direct debit payment process
   * for a given prescription (rxId) and debit permission.
   */
  const handleLastschrift = useCallback(
    async (bill: PatientBillInfoDto, permission?: DebitPermissionDto) => {
      if (!permission) return;
      setDialog({ type: PaymentDialogType.DEBIT, rxId: bill.rxId, permission });
    },
    [setDialog],
  );

  /**
   * External Bill Handling
   */

  const handleExternalBill = useCallback(
    async (bill: PatientBillInfoDto) => {
      setDialog({ type: PaymentDialogType.EXTERNALBILL, rxId: bill.rxId });
    },
    [setDialog],
  );

  /**
   * This function creates a patient transaction
   * Used by the transaction form dialogs
   */
  const handleCreatePatientTransaction = useCallback(
    async (
      bill: PatientBillInfoDto,
      transactionMethod: transactionMethods,
      debitPermission?: DebitPermissionDto,
    ): Promise<void> => {
      if (!userId) {
        notificationStore.showMessage("Benutzer nicht angemeldet.", "error");
        throw new Error("User not logged in");
      }
      if (bill.isEstimated && !bill.isTimeSlotZero) {
        notificationStore.showMessage(
          `Zahlung für Rx-Nr. ${bill.rxNumber} nicht möglich: Bitte planen Sie alle Termine zuerst.`,
          "error",
        );
        throw new Error("Not all appointments scheduled");
      }

      const dto: CreatePatientTransactionDto = {
        amount: Number(bill.dueAmount),
        patientId: patientId,
        rxId: bill.rxId,
        method: transactionMethod,
        text: bill.tarifGroupId !== TarifGroupIds.PRIVATE ? "Zuzahlung" : "Privatzahlung",
        type: transactionTypes.EINNAHME,
        accountId: bill.accountId,
        userId: userId,
        tAccountId: bill.tarifGroupId !== TarifGroupIds.PRIVATE ? 4101 : 4105,
        wasExported: false,
        businessId: bill.businessId,
        locationId: locationId,
        debitPermission:
          transactionMethod === transactionMethods.LASTSCHRIFT ? debitPermission : undefined,
      };

      createTxMut.mutate(dto);
    },
    [patientId, userId, createTxMut],
  );

  /**
   * Refunds the overpaid amount to the patient in cash.
   *
   * Previously this opened the legacy RETURNTOPATIENT confirmation dialog. The new
   * SEPA refund dialog already lets the user pick "Bar" and confirm, so we create the
   * cash refund transaction directly here instead of opening a second dialog.
   * Used in UI: "Bar" option in the SEPA refund dialog (Patient Billing Tab).
   */
  const handlePaybackToPatient = useCallback(
    async (bill: PatientBillInfoDto) => {
      if (!userId) {
        notificationStore.showMessage("Benutzer nicht angemeldet.", "error");
        return;
      }
      if (bill.isEstimated && !bill.isTimeSlotZero) {
        notificationStore.showMessage(
          `Zahlung für Rx-Nr. ${bill.rxNumber} nicht möglich: Bitte planen Sie alle Termine zuerst.`,
          "error",
        );
        return;
      }

      const tAccounts = bill.tarifGroupId !== TarifGroupIds.PRIVATE ? 4101 : 4105;

      const tx: CreatePatientTransactionDto = {
        amount: Number(bill.dueAmount.toFixed(2)),
        method: transactionMethods.CASH,
        patientId: patientId,
        rxId: bill.rxId,
        text: "Rückerstattung des zu viel gezahlten Betrags an den Patienten",
        type: transactionTypes.REFUND,
        accountId: bill.accountId,
        userId: userId,
        tAccountId: tAccounts,
        businessId: bill.businessId,
        locationId: locationId,
      };
      createTxMut.mutate(tx);
    },
    [patientId, userId, locationId, createTxMut],
  );

  /**
   * It creates a transaction to refund the patient based on the provided bill information.
   */
  const handleReturnToPatient = useCallback(
    async (bill: PatientBillInfoDto) => {
      if (!userId) {
        notificationStore.showMessage("Benutzer nicht angemeldet.", "error");
        return;
      }
      const tAccounts = bill.tarifGroupId !== TarifGroupIds.PRIVATE ? 4101 : 4105;

      const tx: CreatePatientTransactionDto = {
        amount: Number(bill.dueAmount.toFixed(2)),
        method: transactionMethods.CASH,
        patientId: patientId,
        rxId: bill.rxId,
        text: "Rückerstattung des zu viel gezahlten Betrags an den Patienten",
        type: transactionTypes.REFUND,
        accountId: bill.accountId,
        userId: userId,
        tAccountId: tAccounts,
        businessId: bill.businessId,
        locationId: locationId,
      };
      createTxMut.mutate(tx);
    },
    [patientId, userId, createTxMut],
  );

  /**
   * This function creates a new rechnung for the Rx
   * Used in UI: "Rechnung Erstellen" button (Patient Billing Tab)
   */
  const handleCreateRechnung = useCallback(
    async (bill: PatientBillInfoDto) => {
      if (!userId) {
        notificationStore.showMessage("Benutzer nicht angemeldet.", "error");
        return;
      }
      if (bill.isEstimated && !bill.isTimeSlotZero) {
        notificationStore.showMessage("Es sind noch nicht alle Termine vergeben.", "error");
        return;
      }

      const dto: CreateRechnungDto = {
        amount: Number(bill.dueAmount.toFixed(2)),
        patientId: patientId,
        rxId: bill.rxId,
        text: bill.tarifGroupId !== TarifGroupIds.PRIVATE ? "Zuzahlung" : "Privatzahlung",
        type: transactionTypes.EINNAHME,
        paymentStatus: paymentStatus.NOT_PAID,
        accountId: bill.accountId,
        userId: userId,
        tAccountId: bill.tarifGroupId !== TarifGroupIds.PRIVATE ? 4101 : 4105,
        pdfDecoration: withLetterhead
          ? CreateRechnungDtoPdfDecorationEnum.Both
          : CreateRechnungDtoPdfDecorationEnum.None,
      };
      createRechnungMut.mutate(dto);
    },
    [patientId, userId, createRechnungMut, withLetterhead],
  );

  /**
   * Handle SEPA button click - either auto-select single permission or show menu
   */
  const handleSepaButtonClick = useCallback(
    (bill: PatientBillInfoDto, validSepaPermissions: DebitPermissionDto[]) => {
      if (!bill?.rxId) return;

      if (validSepaPermissions.length === 1) {
        if (bill && bill?.accountId && bill.accountId !== validSepaPermissions[0].account?.id) {
          notificationStore.showMessage(
            "Debit Permission-Konto und Rx-Konto stimmen nicht überein",
            "warning",
          );
          return;
        }
        handleLastschrift(bill, validSepaPermissions[0]);
      } else {
        setSepaMenuState((prev) => ({
          anchorEl: document.activeElement as HTMLElement,
          openMenus: { ...prev.openMenus, [String(bill.rxId)]: true },
        }));
      }
    },
    [handleLastschrift],
  );

  /**
   * Handle SEPA permission selection from menu
   */
  const handleSepaSelect = useCallback(
    (bill: PatientBillInfoDto, permission: DebitPermissionDto) => {
      if (!bill?.rxId) return;

      if (bill.accountId && bill.accountId !== permission.account?.id) {
        notificationStore.showMessage(
          "Debit Permission-Konto und Rx-Konto stimmen nicht überein",
          "warning",
        );
        return;
      }

      setSepaMenuState((prev) => ({
        ...prev,
        openMenus: { ...prev.openMenus, [String(bill.rxId)]: false },
      }));
      handleLastschrift(bill, permission);
    },
    [handleLastschrift],
  );

  /**
   * Close SEPA menu
   */
  const closeSepaMenu = useCallback((rxId?: string) => {
    if (!rxId) return;
    setSepaMenuState((prev) => ({
      ...prev,
      openMenus: { ...prev.openMenus, [rxId]: false },
    }));
  }, []);

  /**
   * Helper to determine if a row is busy (i.e., a mutation is pending for that rxId)
   */
  const pendingRxId = createRechnungMut.variables?.rxId as string | undefined;

  const isRowBusy = useCallback(
    (rxId?: string) => !!rxId && createRechnungMut.isPending && pendingRxId === rxId,
    [createRechnungMut.isPending, pendingRxId],
  );

  const busy = useMemo(
    () => createRechnungMut.isPending || createTxMut.isPending,
    [createRechnungMut.isPending, createTxMut.isPending],
  );

  return {
    dialog,
    setDialog,
    closeDialogs,

    handlePay,
    handlePaybackToPatient,
    handleLastschrift,
    handleReturnToPatient,
    handleCreateRechnung,
    handleCreatePatientTransaction,

    // SEPA handling
    handleSepaButtonClick,
    handleSepaSelect,
    closeSepaMenu,
    sepaMenuState,
    // External bill handling
    handleExternalBill,

    // PDF letterhead (header/footer bands) toggle
    withLetterhead,
    setWithLetterhead,

    isRowBusy,
    busy,
  };
}
